<template>
  <v-dialog
    v-model="isOpenComputed"
    class="responsive-dialog"
    max-width="850"
    persistent
  >
    <v-card class="responsive-card">
      <v-card-title class="responsive-title">
        <h2 class="modal-title">
          {{ getModalTitle() }}
        </h2>
        <v-icon
          @click="closeModal"
          class="close-icon"
          role="button"
          tabindex="0"
          aria-label="Close dialog"
          @keydown.enter="closeModal"
          @keydown.space.prevent="closeModal"
          >mdi-close</v-icon
        >
      </v-card-title>
      <div style="display: flex; justify-content: center; width: 100%">
        <span style="width: 94%; border: solid 1px #f2f3fc"></span>
      </div>

      <v-card-text>
        <div style="margin-block: 16px">
          <div v-if="error" class="content-section">
            <v-icon size="64" color="error" class="status-icon"
              >mdi-alert-circle</v-icon
            >
            <h1 class="error-title">Something went wrong</h1>
            <p class="error-message">
              {{ error }}
            </p>
          </div>

          <div v-else-if="driveLink" class="content-section">
            <img
              src="/images/google-drive.png"
              alt="Google Drive"
              class="google-drive-image"
            />
            <h1 class="success-title">Successfully added</h1>
            <p class="success-message">
              We've added a copy of the selected resource to your Google Drive.
              <br />
              <small>Google Drive is a trademark of Google LLC.</small>
            </p>
          </div>

          <div v-else class="content-section">
            <img
              src="/images/google-drive.png"
              alt="Google Drive"
              class="google-drive-image"
            />
            <h1 class="main-title">
              {{
                isLoading
                  ? `Copying ${fileTypeInfo.displayName} to your Google Drive...`
                  : `Copy this ${fileTypeInfo.displayName.toLowerCase()} to your Google Drive`
              }}
            </h1>
            <p class="main-message">
              {{
                isLoading
                  ? `Please wait while we copy the ${fileTypeInfo.displayName.toLowerCase()} to your Google Drive account.`
                  : `Allow Eddy to add this ${fileTypeInfo.displayName.toLowerCase()} to your Google Drive. This permission
              allows us to make a copy of this resource within your Google Drive.`
              }}
              <br />
              <small>Google Drive is a trademark of Google LLC.</small>
            </p>
          </div>

          <div class="button-container">
            <button
              class="btn-secondary"
              @click="handleCancelOrBack"
              :disabled="isLoading"
            >
              {{ getSecondaryButtonText() }}
            </button>
            <button
              class="btn-primary"
              @click="handleCopyOrView"
              :disabled="isLoading && !error"
            >
              <template v-if="isLoading && !error">
                <v-progress-circular
                  indeterminate
                  color="white"
                  size="20"
                ></v-progress-circular>
              </template>
              <template v-else>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  v-if="driveLink"
                  class="button-icon"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M2 5.31818C2 4.03741 3.03803 3 4.31818 3H6.34636C6.62251 3 6.84636 3.22386 6.84636 3.5C6.84636 3.77614 6.62251 4 6.34636 4H4.31818C3.59015 4 3 4.58986 3 5.31818V11.6818C3 12.4101 3.59015 13 4.31818 13H10.6818C11.4098 13 12 12.4101 12 11.6818V9.65455C12 9.3784 12.2239 9.15455 12.5 9.15455C12.7761 9.15455 13 9.3784 13 9.65455V11.6818C13 12.9626 11.962 14 10.6818 14H4.31818C3.03803 14 2 12.9626 2 11.6818V5.31818Z"
                    fill="white"
                  />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M9 2.5C9 2.22386 9.22386 2 9.5 2H13.5C13.7761 2 14 2.22386 14 2.5V6.5C14 6.77614 13.7761 7 13.5 7C13.2239 7 13 6.77614 13 6.5V3H9.5C9.22386 3 9 2.77614 9 2.5Z"
                    fill="white"
                  />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M13.4891 2.5269C13.6841 2.72248 13.6836 3.03906 13.488 3.23401L8.85298 7.85401C8.6574 8.04895 8.34082 8.04844 8.14587 7.85286C7.95093 7.65728 7.95144 7.3407 8.14702 7.14575L12.782 2.52575C12.9776 2.33081 13.2942 2.33132 13.4891 2.5269Z"
                    fill="white"
                  />
                </svg>
                {{ getPrimaryButtonText() }}
              </template>
            </button>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, watch, computed, defineProps, defineEmits, onMounted } from "vue";

// CopyGoogleDriveResource Component - Handles copying Google Docs, Sheets, and Slides to user's Google Drive

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  fileUrl: {
    type: String,
    required: true,
  },
  fileName: {
    type: String,
    default: "Resource",
  },
});

const emit = defineEmits(["update:isOpen"]);

const driveLink = ref("");
const isLoading = ref(false);
const error = ref("");

const GOOGLE_CLIENT_ID = process.env.VUE_APP_GOOGLE_CLIENT_ID;
const SCOPES =
  "https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/drive.readonly";

let googleAccessToken = null;
let tokenClient = null;
const isGisInitialized = ref(false);

const fileTypeInfo = computed(() => {
  if (!props.fileUrl) return { type: "file", displayName: "File" };

  const fileInfo = extractFileId(props.fileUrl);
  if (!fileInfo) return { type: "file", displayName: "File" };

  return {
    type: fileInfo.type,
    displayName: getFileTypeDisplayName(fileInfo.type),
  };
});

const isOpenComputed = computed({
  get: () => props.isOpen,
  set: (value) => emit("update:isOpen", value),
});

const loadGoogleIdentityServices = () => {
  return new Promise((resolve, reject) => {
    if (
      typeof window.google !== "undefined" &&
      window.google.accounts &&
      window.google.accounts.oauth2
    ) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (
        typeof window.google !== "undefined" &&
        window.google.accounts &&
        window.google.accounts.oauth2
      ) {
        resolve();
      } else {
        reject(
          new Error(
            "Google Identity Services script loaded but 'window.google.accounts.oauth2' not available."
          )
        );
      }
    };
    script.onerror = () =>
      reject(new Error("Failed to load Google Identity Services script."));
    document.head.appendChild(script);
  });
};

const initializeGisTokenClient = async () => {
  if (!GOOGLE_CLIENT_ID) {
    console.error(
      "Google Client ID is not configured. Set VUE_APP_GOOGLE_CLIENT_ID in your .env file."
    );
    error.value =
      "Google authentication is not configured correctly. Please contact support.";
    isGisInitialized.value = false;
    return;
  }
  try {
    await loadGoogleIdentityServices();

    if (
      typeof window.google === "undefined" ||
      typeof window.google.accounts === "undefined" ||
      typeof window.google.accounts.oauth2 === "undefined"
    ) {
      console.error(
        "Google Identity Services not fully available after script load attempt."
      );
      error.value =
        "Failed to initialize Google Sign-In components. Please try refreshing the page.";
      isGisInitialized.value = false;
      return;
    }

    tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: SCOPES,
      callback: (tokenResponse) => {
        if (tokenResponse && tokenResponse.access_token) {
          googleAccessToken = tokenResponse.access_token;
          if (isLoading.value) {
            performCopyOperation();
          }
        } else {
          console.error(
            "Error: No access token received or token response error.",
            tokenResponse
          );
          error.value =
            "Failed to obtain Google authorization. Please try again.";
          isLoading.value = false;
        }
      },
      error_callback: (gisError) => {
        console.error("GIS Token Client Error:", gisError);
        error.value = `Google Sign-In error: ${
          gisError.type || "Failed to authorize."
        }. Please try again.`;
        isLoading.value = false;
      },
    });
    isGisInitialized.value = true;
  } catch (err) {
    console.error("Error initializing Google Identity Services:", err);
    error.value =
      "Failed to initialize Google Sign-In. Please check your connection or try again later.";
    isGisInitialized.value = false;
  }
};

const extractFileId = (url) => {
  // Support Google Slides/Presentations
  let match = url.match(/\/presentation\/d\/([a-zA-Z0-9-_]+)/);
  if (match) return { fileId: match[1], type: "presentation" };

  // Support Google Docs/Documents
  match = url.match(/\/document\/d\/([a-zA-Z0-9-_]+)/);
  if (match) return { fileId: match[1], type: "document" };

  // Support Google Sheets/Spreadsheets
  match = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
  if (match) return { fileId: match[1], type: "spreadsheet" };

  // Support generic Google Drive file URLs
  match = url.match(/\/file\/d\/([a-zA-Z0-9-_]+)/);
  if (match) return { fileId: match[1], type: "file" };

  return null;
};

const getFileTypeDisplayName = (type) => {
  switch (type) {
    case "presentation":
      return "Slide Deck";
    case "document":
      return "Document";
    case "spreadsheet":
      return "Spreadsheet";
    default:
      return "File";
  }
};

const generateDriveLink = (fileId, type) => {
  switch (type) {
    case "presentation":
      return `https://docs.google.com/presentation/d/${fileId}/edit`;
    case "document":
      return `https://docs.google.com/document/d/${fileId}/edit`;
    case "spreadsheet":
      return `https://docs.google.com/spreadsheets/d/${fileId}/edit`;
    default:
      return `https://drive.google.com/file/d/${fileId}/view`;
  }
};

const GENERIC_COPY_NAMES = new Set(["", "Resource", "Slide Deck"]);

const getOriginalDriveFileName = async (fileId) => {
  const response = await fetch(
    `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(
      fileId
    )}?fields=name&supportsAllDrives=true`,
    {
      headers: {
        Authorization: `Bearer ${googleAccessToken}`,
      },
    }
  );

  if (!response.ok) {
    return "";
  }

  const data = await response.json();
  return (data.name || "").trim();
};

const resolveCopyFileName = async (fileId) => {
  const providedName = (props.fileName || "").trim();
  if (providedName && !GENERIC_COPY_NAMES.has(providedName)) {
    return providedName;
  }

  const originalName = await getOriginalDriveFileName(fileId);
  return originalName || providedName || "Slide Deck";
};

const performCopyOperation = async () => {
  if (!googleAccessToken) {
    error.value =
      "Google authorization token is missing. Please Sign In again.";
    isLoading.value = false;
    return;
  }

  const fileInfo = extractFileId(props.fileUrl);
  if (!fileInfo) {
    error.value =
      "Invalid Google Drive URL format. Please ensure the URL is from Google Docs, Sheets, or Slides.";
    isLoading.value = false;
    return;
  }

  isLoading.value = true;
  error.value = "";

  try {
    const copyName = await resolveCopyFileName(fileInfo.fileId);
    const response = await fetch(
      `https://www.googleapis.com/drive/v3/files/${fileInfo.fileId}/copy?supportsAllDrives=true`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${googleAccessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: copyName,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      console.error("Google Drive API Error:", errorData);
      let errorMessage = `Error copying file: ${
        errorData.error.message || response.statusText
      }`;
      if (response.status === 401 || response.status === 403) {
        errorMessage +=
          " Your session might have expired or permissions are insufficient. Please try signing in again.";
        googleAccessToken = null;
      }
      throw new Error(errorMessage);
    }

    const copiedFile = await response.json();
    driveLink.value = generateDriveLink(copiedFile.id, fileInfo.type);
  } catch (err) {
    console.error("Error copying file to Google Drive:", err);
    if (err.message && err.message.includes("popup_blocked_by_browser")) {
      error.value =
        "Please disable popup blockers for this site and try again.";
    } else {
      error.value =
        err.message ||
        "An unexpected error occurred while copying the file. Please try again.";
    }
  } finally {
    isLoading.value = false;
  }
};

const copyFileToDrive = async () => {
  if (!props.fileUrl) {
    error.value = "No file URL provided.";
    return;
  }

  if (!isGisInitialized.value || !tokenClient) {
    error.value =
      "Google Sign-In is not ready. Please wait or refresh the page.";
    if (!isGisInitialized.value) await initializeGisTokenClient();
    if (!isGisInitialized.value || !tokenClient) return;
  }

  isLoading.value = true;
  error.value = "";

  if (googleAccessToken) {
    performCopyOperation();
  } else {
    if (tokenClient) {
      tokenClient.requestAccessToken({ prompt: "consent" });
    } else {
      error.value =
        "Google Sign-In client is not available. Please try refreshing.";
      isLoading.value = false;
    }
  }
};

const getModalTitle = () => {
  if (error.value) return "Error";
  if (driveLink.value) return "Success!";
  return "Add Resource to Google Drive";
};

const getPrimaryButtonText = () => {
  if (error.value) return "Try Again";
  if (driveLink.value) return "View in Drive";
  return "Add to My Drive";
};

const getSecondaryButtonText = () => {
  if (driveLink.value || error.value) return "Close";
  return "Cancel";
};

const closeModal = () => {
  isOpenComputed.value = false;
};

const handleCancelOrBack = () => {
  if (driveLink.value || error.value) {
    closeModal();
  } else {
    isLoading.value = false;
    closeModal();
  }
};

const handleCopyOrView = () => {
  if (error.value) {
    error.value = "";
    if (!isGisInitialized.value) {
      initializeGisTokenClient().then(() => {
        if (isGisInitialized.value) copyFileToDrive();
      });
    } else {
      copyFileToDrive();
    }
  } else if (driveLink.value) {
    window.open(driveLink.value, "_blank");
    closeModal();
  } else {
    copyFileToDrive();
  }
};

watch(
  () => props.isOpen,
  (newValue) => {
    if (newValue) {
      driveLink.value = "";
      isLoading.value = false;
      error.value = "";
      if (!isGisInitialized.value) {
        initializeGisTokenClient();
      }
    }
  }
);

onMounted(() => {
  if (!isGisInitialized.value) {
    initializeGisTokenClient();
  }
});
</script>

<style scoped>
/* Responsive Dialog */
.responsive-dialog {
  margin: 16px;
}

.responsive-card {
  width: 100%;
  margin: 0 auto;
}

/* Header Styles */
.responsive-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
}

.modal-title {
  color: var(--primary-blue);
  font-weight: 600;
  font-size: clamp(18px, 3vw, 24px);
  margin: 0;
}

.close-icon {
  cursor: pointer;
  font-size: var(--fs-xxl);
}

/* Content Sections */
.content-section {
  width: 100%;
  text-align: center;
  padding: 0 16px;
}

.status-icon {
  margin-bottom: 16px;
}

/* Responsive Typography */
.error-title {
  color: #d32f2f;
  font-weight: 600;
  margin-bottom: 16px;
  font-size: clamp(24px, 4vw, 32px);
}

.success-title {
  color: #00925e;
  font-weight: 600;
  font-size: clamp(28px, 5vw, 40px);
  margin: 0 0 16px 0;
}

.main-title {
  color: #00925e;
  font-weight: 600;
  font-size: clamp(28px, 5vw, 40px);
  margin: 0 0 16px 0;
  line-height: 1.2;
}

.error-message,
.success-message,
.main-message {
  font-size: clamp(14px, 2.5vw, 16px);
  font-weight: 400;
  color: #384648;
  max-width: 80%;
  margin: 0 auto;
  line-height: 1.5;
}

/* Responsive Image */
.google-drive-image {
  width: clamp(60px, 15vw, 100px);
  height: auto;
  margin-bottom: 16px;
}

/* Button Container */
.button-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;
  flex-wrap: wrap;
}

/* Button Styles */
.btn-secondary {
  color: #e00069;
  background-color: #fff;
  font-size: clamp(16px, 2.5vw, 20px);
  padding: 12px 20px;
  border-radius: 16px;
  font-weight: 600;
  border: 1px solid #e00069;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
  white-space: nowrap;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #e00069;
  color: white;
}

.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background-color: #00925e;
  color: white;
  font-size: clamp(16px, 2.5vw, 20px);
  padding: 12px 20px;
  border-radius: 16px;
  font-weight: 600;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 160px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  white-space: nowrap;
}

.btn-primary:hover:not(:disabled) {
  background-color: #007a4d;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.button-icon {
  margin-right: 8px;
  flex-shrink: 0;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .responsive-title {
    padding: 12px;
  }

  .modal-title {
    font-size: 18px;
  }

  .close-icon {
    font-size: var(--fs-xl);
  }

  .content-section {
    padding: 0 12px;
  }

  .error-title {
    font-size: var(--fs-xl);
  }

  .success-title,
  .main-title {
    font-size: var(--fs-xxl);
  }

  .error-message,
  .success-message,
  .main-message {
    font-size: var(--fs-small);
    max-width: 90%;
  }

  .google-drive-image {
    width: 60px;
  }

  .button-container {
    flex-direction: column;
    gap: 12px;
    margin-top: 20px;
  }

  .btn-secondary,
  .btn-primary {
    width: 100%;
    max-width: 280px;
    font-size: var(--fs-medium);
  }
}

/* Small Mobile Styles */
@media (max-width: 480px) {
  .responsive-dialog {
    margin: 8px;
  }

  .responsive-title {
    padding: 8px;
  }

  .modal-title {
    font-size: var(--fs-medium);
  }

  .error-title {
    font-size: var(--fs-big);
  }

  .success-title,
  .main-title {
    font-size: var(--fs-xl);
  }

  .error-message,
  .success-message,
  .main-message {
    font-size: 13px;
    max-width: 95%;
  }

  .google-drive-image {
    width: 50px;
  }

  .btn-secondary,
  .btn-primary {
    font-size: var(--fs-small);
    padding: 10px 16px;
  }
}

/* Large Desktop Styles */
@media (min-width: 1200px) {
  .content-section {
    padding: 0 24px;
  }

  .error-message,
  .success-message,
  .main-message {
    max-width: 70%;
  }
}
</style>
